import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket, WS_EVENTS } from '@/lib/socket';
import type { BidUpdatePayload, AuctionClosedPayload } from '@/lib/socket';
import { queryKeys } from './useAuctions';
import type { AuctionDetail } from '@/types/domain';

/**
 * Подписка на real-time события конкретного аукциона.
 * Обновляет кэш query, поэтому UI реагирует без рефетча.
 */
export function useAuctionSocket(auctionId: string | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!auctionId) return;

    const socket = getSocket();

    socket.emit(WS_EVENTS.JOIN, { auctionId });

    const onBidUpdate = (data: BidUpdatePayload) => {
      if (data.auction_id !== auctionId) return;

      qc.setQueryData<AuctionDetail>(queryKeys.auction(auctionId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          current_price: data.current_price,
          bids_count: data.bids_count,
          current_leader_id: data.current_leader_id,
          ends_at: data.ends_at,
        };
      });

      qc.invalidateQueries({
        queryKey: queryKeys.bids(auctionId),
        refetchType: 'active',
      });
    };

    // ── Аукцион закрылся ──
    const onClosed = (data: AuctionClosedPayload) => {
      if (data.auction_id !== auctionId) return;

      qc.setQueryData<AuctionDetail>(queryKeys.auction(auctionId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: data.status as AuctionDetail['status'],
          ends_at: new Date().toISOString(),
        };
      });

      void qc.invalidateQueries({ queryKey: queryKeys.auction(auctionId) });
      void qc.invalidateQueries({ queryKey: ['auctions'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['my-auctions'] });
    };

    socket.on(WS_EVENTS.BID_UPDATE, onBidUpdate);
    socket.on(WS_EVENTS.CLOSED, onClosed);

    return () => {
      socket.emit(WS_EVENTS.LEAVE, { auctionId });
      socket.off(WS_EVENTS.BID_UPDATE, onBidUpdate);
      socket.off(WS_EVENTS.CLOSED, onClosed);
    };
  }, [auctionId, qc]);
}